import { BaseElement } from 'base';
import { bus } from 'bus';
import { analytics } from 'analytics-kit';
import { analyticsTarget } from 'analytics-target';
import {
  ANALYTICS_TOPIC,
  ANALYTICS_EVENTS,
  ANALYTICS_BUS_EVENTS,
  ANALYTICS_SCROLL_MILESTONES,
  ANALYTICS_CONTENT_RATIO,
  ANALYTICS_CONTENT_CENTER,
  ANALYTICS_LEVELS,
  ANALYTICS_OVERLAY_TOPICS,
  ANALYTICS_OPEN_OVERLAY,
  ANALYTICS_OPENER_MS,
  ANALYTICS_CLICKABLE,
  ANALYTICS_PRODUCT_ACTIONS,
  ANALYTICS_VALUE_INPUTS,
  ANALYTICS_TEXT_INPUTS,
  ANALYTICS_CHOICE_INPUT,
  ANALYTICS_TEXT_VALUES,
  ANALYTICS_INPUTS,
  ANALYTICS_INSPECT_TOPIC,
  ANALYTICS_HOLD_TOPIC
} from 'analytics-events';
import { THEME_SCRIPT_PREFIX } from 'section-ids';

const SECTION = '.shopify-section';
const SECTION_CTX = '[data-analytics-section]';
const BLOCK_CTX = '[data-analytics-block]';
const PRODUCT_CTX = '[data-analytics-product]';
const OVERLAY_CTX = '[data-analytics-overlay]';
const CONTENT_CTX = '[data-analytics-content]';
const CONTENT = `${SECTION}, ${BLOCK_CTX}, ${CONTENT_CTX}:not([data-analytics-content-passive='true'])`;
const IGNORE = '[data-analytics-ignore]';
const FLASH_MS = 800;
const TRACKED = 'data-analytics-tracked';
const NO_PRODUCT = 'data-analytics-no-product';
const INERT = ':disabled, [aria-disabled="true"], [aria-busy="true"]';
const LCP_ENTRY = 'largest-contentful-paint';
const FCP_ENTRY = 'first-contentful-paint';

class AnalyticsPage extends BaseElement {
  #observers = [];
  #watched = new WeakMap();
  #owners = new WeakMap();
  #overlays = new Map();
  #opener = null;
  #scrollFrame = 0;
  #scrollDone = new AbortController();
  #scanFrame = 0;
  #hold = false;
  #flashes = new Set();
  #lcp = null;
  #loadTimer = 0;

  onConnect() {
    if (!analytics.start()) return;

    this.listen(this, 'click', this.#onClick, { capture: true });
    this.listen(this, 'change', this.#onChange, { capture: true });
    this.listen(window, 'scroll', this.#onScroll, {
      passive: true,
      signal: this.#scrollDone.signal
    });
    this.listen(window, 'error', this.#onError);
    this.subscribe(ANALYTICS_TOPIC, this.#onBus);
    Object.entries(ANALYTICS_OVERLAY_TOPICS).forEach(([topic, overlay]) =>
      this.subscribe(topic, value => this.#onOverlay(overlay, value))
    );
    this.#watchContent();
    this.#watchLoad();
    this.cleanup(() => {
      cancelAnimationFrame(this.#scrollFrame);
      cancelAnimationFrame(this.#scanFrame);
      this.#flashes.forEach(clearTimeout);
    });
    if (analytics.debugging) this.#startDebug();
  }

  get events() {
    return analytics.history.map(entry => entry.payload);
  }

  explain(element) {
    if (this.#trackable(element)) {
      return analytics.preview(
        ANALYTICS_EVENTS.inputChange,
        this.#changeParams(element)
      );
    }
    const control = this.#owner(element, ANALYTICS_CLICKABLE);
    if (control) {
      return analytics.preview(
        ANALYTICS_EVENTS.click,
        this.#clickParams(control)
      );
    }
    return this.#previewView(element);
  }

  inventory() {
    const tracked = selector =>
      this.qa(selector).filter(element => !this.#owner(element, IGNORE));
    return [
      ...tracked(`${CONTENT}, ${OVERLAY_CTX}`).map(element =>
        this.#previewView(element)
      ),
      ...tracked(ANALYTICS_CLICKABLE).map(element => this.explain(element)),
      ...tracked(ANALYTICS_INPUTS)
        .filter(element => this.#trackable(element))
        .map(element => this.explain(element))
    ];
  }

  #previewView(element) {
    const overlay = element.dataset.analyticsOverlay;
    return analytics.preview(
      ANALYTICS_EVENTS.contentView,
      overlay ? this.#overlayParams(overlay) : this.#viewParams(element)
    );
  }

  #clickParams(control) {
    return {
      action: this.#action(control),
      value: control.dataset.analyticsValue ?? this.#toggled(control),
      from: this.#context(control).from,
      target: this.#target(control)
    };
  }

  #changeParams(input) {
    return {
      action:
        input.dataset.analyticsAction ?? (input.getAttribute('name') || null),
      value: input.dataset.analyticsValue ?? this.#value(input),
      from: this.#context(input).from,
      target: this.#target(input)
    };
  }

  #viewParams(element, { from, level } = this.#context(element)) {
    return { level, from };
  }

  #overlayParams(overlay, opener = null) {
    return {
      level: ANALYTICS_LEVELS.overlay,
      from: opener?.from ?? null,
      target: { overlay, product: opener?.product ?? null }
    };
  }

  #startDebug() {
    this.toggleAttribute('data-debug', true);
    this.listen(this, 'pointerover', this.#onInspect);
    this.subscribe(ANALYTICS_HOLD_TOPIC, hold => {
      this.#hold = Boolean(hold);
    });
    import('analytics-debug').catch(error =>
      console.error('[analytics-page] debug panel failed to load:', error)
    );
  }

  #markTracked() {
    const mark = (elements, event) =>
      elements
        .filter(element => !this.#owner(element, IGNORE))
        .forEach(element => element.setAttribute(TRACKED, event));
    mark(this.qa(ANALYTICS_CLICKABLE), ANALYTICS_EVENTS.click);
    this.qa('[data-analytics-action]').forEach(control =>
      control.toggleAttribute(
        NO_PRODUCT,
        ANALYTICS_PRODUCT_ACTIONS.includes(control.dataset.analyticsAction) &&
          !this.#owner(control, PRODUCT_CTX)
      )
    );
    mark(
      this.qa(ANALYTICS_INPUTS).filter(element => this.#trackable(element)),
      ANALYTICS_EVENTS.inputChange
    );
  }

  #onInspect(event) {
    if (this.#owner(event.target, IGNORE)) return;
    const control = this.#owner(event.target, ANALYTICS_CLICKABLE);
    bus.publish(
      ANALYTICS_INSPECT_TOPIC,
      control && this.#inert(control) ? null : this.explain(event.target)
    );
  }

  #flash(element) {
    if (!analytics.debugging || !element?.isConnected) return;
    element.toggleAttribute('data-analytics-flash', true);
    const timer = setTimeout(() => {
      this.#flashes.delete(timer);
      element.removeAttribute('data-analytics-flash');
    }, FLASH_MS);
    this.#flashes.add(timer);
  }

  #onClick(event) {
    const control = this.#owner(event.target, ANALYTICS_CLICKABLE);
    if (!control || this.#owner(control, IGNORE) || this.#inert(control))
      return;
    const params = this.#clickParams(control);
    analytics.track(ANALYTICS_EVENTS.click, params);
    this.#keepOpener(params);
    this.#flash(control);
    if (this.#hold && control.matches('a[href]')) event.preventDefault();
  }

  #keepOpener({ from, target }) {
    if (!target?.overlay) return;
    this.#opener = {
      overlay: target.overlay,
      from,
      product: target.product,
      time: performance.now()
    };
  }

  #takeOpener(overlay) {
    const opener = this.#opener;
    if (
      opener?.overlay !== overlay ||
      performance.now() - opener.time > ANALYTICS_OPENER_MS
    )
      return null;
    this.#opener = null;
    return opener;
  }

  #inert(control) {
    if (control.matches(INERT)) return true;
    for (
      let host = control.parentElement;
      host && host !== this;
      host = host.parentElement
    ) {
      if (host.localName.includes('-') && host.matches('[aria-busy="true"]'))
        return true;
    }
    return false;
  }

  #onChange(event) {
    const input = event.target;
    if (!this.#trackable(input) || this.#owner(input, IGNORE)) return;
    analytics.track(ANALYTICS_EVENTS.inputChange, this.#changeParams(input));
    this.#flash(input);
  }

  #watchLoad() {
    let observer = null;
    if (PerformanceObserver.supportedEntryTypes?.includes(LCP_ENTRY)) {
      observer = new PerformanceObserver(list =>
        this.#keepLcp(list.getEntries())
      );
      observer.observe({ type: LCP_ENTRY, buffered: true });
      this.cleanup(() => observer.disconnect());
    }
    const send = () => {
      this.#loadTimer = setTimeout(() => {
        if (observer) this.#keepLcp(observer.takeRecords());
        this.#onLoad();
      });
    };
    if (document.readyState === 'complete') send();
    else this.listen(window, 'load', send, { once: true });
    this.cleanup(() => clearTimeout(this.#loadTimer));
  }

  #keepLcp(entries) {
    const latest = entries.at(-1);
    if (latest) this.#lcp = latest.startTime;
  }

  #onLoad() {
    if (!analytics.once(ANALYTICS_EVENTS.pageLoad)) return;
    const [navigation] = performance.getEntriesByType('navigation');
    const [paint] = performance.getEntriesByName(FCP_ENTRY);
    const toMs = time => (time > 0 ? Math.round(time) : null);
    const timing = {
      ttfb: toMs(navigation?.responseStart),
      fcp: toMs(paint?.startTime),
      lcp: toMs(this.#lcp),
      dom_ready: toMs(navigation?.domContentLoadedEventEnd),
      load: toMs(navigation?.loadEventEnd)
    };
    analytics.track(ANALYTICS_EVENTS.pageLoad, { value: timing.load, timing });
  }

  #onScroll() {
    if (this.#scrollFrame) return;
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      const { scrollHeight } = document.documentElement;
      const bottom = Math.ceil(window.scrollY + window.innerHeight) + 1;
      const depth = (bottom / scrollHeight) * 100;
      ANALYTICS_SCROLL_MILESTONES.forEach(milestone => {
        if (depth < milestone || !analytics.once(`scroll:${milestone}`)) return;
        analytics.track(ANALYTICS_EVENTS.scroll, { value: milestone });
      });
      if (depth >= ANALYTICS_SCROLL_MILESTONES.at(-1)) this.#scrollDone.abort();
    });
  }

  #onError({ filename }) {
    if (!filename?.includes(THEME_SCRIPT_PREFIX)) return;
    analytics.track(ANALYTICS_EVENTS.error, { action: 'script_error' });
  }

  #onBus(message) {
    if (!message?.id || !ANALYTICS_BUS_EVENTS.includes(message.event)) return;
    if (!analytics.once(`bus:${message.id}`)) return;

    const source =
      message.source instanceof Element && this.contains(message.source)
        ? message.source
        : null;
    analytics.track(message.event, {
      action: message.action,
      value: message.value,
      from: source ? this.#context(source).from : null,
      target: message.target ?? (source ? this.#target(source) : null)
    });
    this.#flash(source);
  }

  #watchContent() {
    const byRatio = new IntersectionObserver(
      entries => this.#onIntersect(entries, ANALYTICS_CONTENT_RATIO),
      { threshold: ANALYTICS_CONTENT_RATIO }
    );
    const byCenter = new IntersectionObserver(
      entries => this.#onIntersect(entries, 0),
      { rootMargin: ANALYTICS_CONTENT_CENTER }
    );
    this.#observers = [byRatio, byCenter];
    this.cleanup(() =>
      this.#observers.forEach(observer => observer.disconnect())
    );
    this.observe(new MutationObserver(() => this.#scheduleScan()), this, {
      childList: true,
      subtree: true
    });
    this.#scan();
  }

  #scheduleScan() {
    if (this.#scanFrame) return;
    this.#scanFrame = requestAnimationFrame(() => {
      this.#scanFrame = 0;
      this.#scan();
    });
  }

  #scan() {
    if (analytics.debugging) this.#markTracked();
    this.qa(CONTENT).forEach(element => {
      if (this.#watched.has(element)) return;
      const nodes =
        getComputedStyle(element).display === 'contents'
          ? [...element.children]
          : [element];
      this.#watched.set(element, nodes);
      nodes.forEach(node => {
        this.#owners.set(node, element);
        this.#observers.forEach(observer => observer.observe(node));
      });
    });
  }

  #onIntersect(entries, ratio) {
    entries.forEach(entry => {
      if (!entry.isIntersecting || entry.intersectionRatio < ratio) return;
      const { width, height } = entry.boundingClientRect;
      if (!width || !height) return;

      const element = this.#owners.get(entry.target);
      if (!element) return;
      this.#watched
        .get(element)
        ?.forEach(node =>
          this.#observers.forEach(observer => observer.unobserve(node))
        );

      const context = this.#context(element);
      if (!analytics.once(`content:${context.key}`)) return;
      analytics.track(
        ANALYTICS_EVENTS.contentView,
        this.#viewParams(element, context)
      );
    });
  }

  #onOverlay(overlay, value) {
    const instance = typeof value === 'string' ? value : '';
    const shown = value ? instance : null;
    if (this.#overlays.get(overlay) === shown) return;
    this.#overlays.set(overlay, shown);
    if (shown === null) return;

    analytics.track(
      ANALYTICS_EVENTS.contentView,
      this.#overlayParams(overlay, this.#takeOpener(overlay))
    );
  }

  #context(element) {
    const section = this.#owner(element, SECTION);
    const sectionCtx =
      this.#owner(element, SECTION_CTX) ??
      (section
        ? this.qa(SECTION_CTX).find(ctx => ctx.closest(SECTION) === section)
        : null);
    const block = this.#owner(element, BLOCK_CTX);
    const content = this.#owner(element, CONTENT_CTX);
    const overlay = this.#owner(element, OVERLAY_CTX);
    const blockKey = block?.dataset.analyticsBlockId ?? 'section';
    const contentKey = content
      ? `:${content.dataset.analyticsContent}:${content.dataset.analyticsContentId || content.dataset.analyticsContentIndex}`
      : '';

    return {
      key: `${section?.id ?? 'layout'}:${blockKey}${contentKey}`,
      level: content
        ? ANALYTICS_LEVELS.content
        : block
          ? ANALYTICS_LEVELS.block
          : ANALYTICS_LEVELS.section,
      from: {
        section_type: sectionCtx?.dataset.analyticsSection ?? null,
        section_index: section ? this.qa(SECTION).indexOf(section) + 1 : null,
        block_type: block?.dataset.analyticsBlock ?? null,
        block_index: this.#index(block?.dataset.analyticsBlockIndex),
        content_type: content?.dataset.analyticsContent ?? null,
        content_index: this.#index(content?.dataset.analyticsContentIndex),
        content_id: content?.dataset.analyticsContentId || null,
        overlay:
          overlay &&
          overlay.dataset.analyticsOverlay !== element.dataset?.analyticsOpens
            ? overlay.dataset.analyticsOverlay
            : null
      }
    };
  }

  #index(raw) {
    const index = Number(raw);
    return Number.isInteger(index) && index > 0 ? index : null;
  }

  #target(element) {
    const link = this.#owner(element, 'a[href]');
    const href =
      link?.getAttribute('href') ??
      (element.type === 'submit' ? element.form?.getAttribute('action') : null);
    const place = href
      ? analyticsTarget(href, window.location.href, window.routes?.root_url)
      : null;
    const product = this.#product(this.#owner(element, PRODUCT_CTX));
    const overlay = this.#opens(element);

    if (!place && !product && !overlay) return null;
    return { ...place, overlay, product };
  }

  #opens(control) {
    if (this.#toggled(control) === 'closed') return null;
    if (control.dataset.analyticsOpens) return control.dataset.analyticsOpens;
    const id = control.getAttribute('aria-controls');
    const panel = id ? this.q(`#${CSS.escape(id)}`) : null;
    const overlay = (
      panel?.closest(OVERLAY_CTX) ?? panel?.querySelector(OVERLAY_CTX)
    )?.dataset.analyticsOverlay;
    const inside = this.#owner(control, OVERLAY_CTX)?.dataset.analyticsOverlay;
    return overlay && overlay !== inside ? overlay : null;
  }

  #product(host) {
    if (!host) return null;
    try {
      return JSON.parse(host.dataset.analyticsProduct);
    } catch {
      return null;
    }
  }

  #action(control) {
    if (control.dataset.analyticsAction) return control.dataset.analyticsAction;
    if (this.#opens(control)) return ANALYTICS_OPEN_OVERLAY;
    if (this.#toggled(control) !== null) return 'toggle';
    if (control.getAttribute('role') === 'tab') return 'select_tab';
    if (control.matches('a[href]')) return 'navigate';
    if (control.type === 'submit' && control.form) return 'submit';
    return 'press';
  }

  #toggled(control) {
    const expanded =
      control.localName === 'summary'
        ? String(Boolean(control.parentElement?.open))
        : control.getAttribute('aria-expanded');
    if (expanded !== null) return expanded === 'true' ? 'closed' : 'open';

    const pressed =
      control.getAttribute('aria-pressed') ??
      control.getAttribute('aria-checked');
    if (pressed !== null) return pressed === 'true' ? 'off' : 'on';
    return null;
  }

  #trackable(element) {
    return (
      element instanceof Element &&
      element.matches(ANALYTICS_INPUTS) &&
      (ANALYTICS_VALUE_INPUTS.includes(element.type) ||
        ANALYTICS_TEXT_INPUTS.includes(element.type))
    );
  }

  #value(input) {
    const choice = input.dataset.analyticsInput === ANALYTICS_CHOICE_INPUT;
    if (!choice && ANALYTICS_TEXT_INPUTS.includes(input.type)) {
      return input.value.trim()
        ? ANALYTICS_TEXT_VALUES.filled
        : ANALYTICS_TEXT_VALUES.empty;
    }
    if (input.type === 'checkbox') return input.checked;
    if (input.type === 'select-multiple') {
      return [...input.selectedOptions].map(option => option.value).join(',');
    }
    if (input.type === 'range' || input.type === 'number') {
      return input.value === '' ? null : Number(input.value);
    }
    return input.value;
  }

  #owner(element, selector) {
    const found = element?.closest?.(selector);
    return found && this.contains(found) ? found : null;
  }
}

if (!customElements.get('analytics-page')) {
  customElements.define('analytics-page', AnalyticsPage);
}
